import { Box } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import {
  getPresignedURLForPut,
  fileUploadToS3,
  IBulkEmailAttachment,
  composeEmailService,
  amplitude,
} from 'wisestep-api';
import { EmailForm, IUserDTO, WsDrawer } from 'wisestep-base';

import { useUserContext, useWorkspaceContext } from '../../context';
import { warning, success } from '../../utils/NotificationMessages';
import { useNotificationStore } from '../../utils/NotificationStore';
import { openUrlInNewTab } from '../../utils/openUrlInNewTab';
import { useFetchRecruiterSMTPAccounts } from '../BulkEmail/helper/apiCalls';

import AttachmentWarningDialog from './AttachmentWarningDialog';
import DiscardForm from './DiscardForm';

interface IEmailFormData {
  attachments?: IBulkEmailAttachment[];
  toEmailList: string[];
  ccList?: string[];
  bccList?: string[];
  subject: string;
  body?: string;
}
interface ComposeEmailProps {
  showDrawer: boolean;
  emailData?: IEmailFormData;
  onClose(): void;
  onAttachmentWarningClose(): void;
  hasAttachments: boolean;
}

const ComposeEmail = ({
  showDrawer,
  emailData,
  onClose,
  hasAttachments = false,
  onAttachmentWarningClose,
}: ComposeEmailProps) => {
  const workspaceContext = useWorkspaceContext();
  const workspace = useWorkspaceContext();
  const defaultData = {
    attachments: emailData && emailData.attachments ? emailData.attachments : [],
    toEmailList: emailData && emailData.toEmailList ? emailData.toEmailList : [],
    subject: emailData && emailData.subject ? emailData.subject : '',
    ccList: emailData && emailData.ccList ? emailData.ccList : [],
    bccList: emailData && emailData.bccList ? emailData.bccList : [],
    body: emailData && emailData.body ? emailData.body : '',
  };
  const setNotification = useNotificationStore((state) => state.setNotification);

  const [formState, setFormState] = useState<boolean>(false);
  const [dialog, setDialog] = useState<boolean>(false);
  const [emailFormData, setEmailFormData] = useState<IEmailFormData>(defaultData);
  const [fromEmailSignature, setFromEmailSignature] = useState<string>('');
  const accountData = useFetchRecruiterSMTPAccounts();
  const loggedInUser = useUserContext().userDTO as IUserDTO;

  const smtpAccounts = accountData.data;

  useEffect(() => {
    if (smtpAccounts) setFromEmailSignature(smtpAccounts[0]?.signatureText ?? '');
  }, [smtpAccounts]);

  const sendEmail = async (data) => {
    amplitude('Inbox_Send', {
      clickedOn: 'Send',
      workspaceName: workspace.name,
      pageName: 'Mail',
      planType: workspace.planType,
      interfaceType: 'web',
      pageUrl: window.location.href,
    });
    try {
      await composeEmailService({
        from: data.from,
        fromName: '',
        to: data.to,
        subject: data.subject,
        cc: data.cc,
        bcc: data.bcc,
        body: data.body,
        recruiterId: loggedInUser.email ?? '',
        attachments: emailFormData?.attachments ? emailFormData.attachments : [],
      });
      setNotification(success('Email sent successfully'));
      setEmailFormData({
        attachments: [],
        toEmailList: [],
        subject: '',
        ccList: [],
        bccList: [],
        body: '',
      });
      setFormState(false);
      onClose();
    } catch (error: any) {
      setNotification(warning(error.message));
    }
  };
  const upload = async (file: File) => {
    if (emailFormData.attachments) {
      try {
        const response = await getPresignedURLForPut({ fileName: file.name, fileType: file.type });
        const tempData = emailFormData;
        tempData.attachments = [
          ...emailFormData.attachments,
          ...[
            {
              attachementName: file.name,
              attachementURL: response.data.preSignedUrl.split('?')[0],
            },
          ],
        ];
        setEmailFormData(tempData);
        await fileUploadToS3(file, response.data.preSignedUrl);
        setNotification(success('Uploaded attachment(s) successfully!'));
      } catch (err: any) {
        console.log(err.message);
        setNotification(warning(err.message));
      }
    }
  };

  const fromEmailSignatureUpdater = (fEmail: string) => {
    const signatureText =
      smtpAccounts
        .filter((account) => account.fromEmail == fEmail)
        .map((data) => data.signatureText)[0] ?? '';
    setFromEmailSignature(signatureText);
  };
  return (
    <Box>
      <WsDrawer open={showDrawer} onClose={() => (formState ? setDialog(!dialog) : onClose())}>
        <EmailForm
          template={emailData?.body}
          submitAction={sendEmail}
          toEmailList={emailData?.toEmailList}
          fromEmailList={
            !isEmpty(smtpAccounts) ? smtpAccounts.map((account) => account.fromEmail) : []
          }
          bccList={emailData?.bccList}
          subjectTemplate={emailData?.subject}
          uploadAttachment={upload}
          handleClose={onClose}
          fromEmailSignature={fromEmailSignature}
          updateFromEmailSignature={(email: string) => fromEmailSignatureUpdater(email)}
          handleAttachmentError={() =>
            setNotification(warning('The file size must be less than 25MB!'))
          }
          isDirtyCallback={(value) => setFormState(value)}
          manageSenderAccounts={() =>
            openUrlInNewTab(`https://${workspaceContext.link}.wisestep.com/email-settings/`)
          }
          deleteAttachmentHandler={(deletedFileName: string) => {
            const tempData = emailFormData;
            if (emailFormData.attachments) {
              tempData.attachments = emailFormData.attachments.filter(
                (singleAttachment) => singleAttachment.attachementName !== deletedFileName
              );
              setEmailFormData(tempData);
            }
          }}
        />

        <DiscardForm
          openDialog={dialog}
          closeDialog={() => setDialog(!dialog)}
          handler={() => {
            onClose();
            setDialog(!dialog);
          }}
        />
        {showDrawer && (
          <AttachmentWarningDialog
            openDialog={hasAttachments}
            closeDialog={onAttachmentWarningClose}
          />
        )}
      </WsDrawer>
    </Box>
  );
};

export default ComposeEmail;
